// @ts-nocheck
import create from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { axios } from "./axios";
import { showError, showSuccess } from "../actions/globalActions";
import { browserTimeZone } from "../utils/registrationDateTz";

const baseUrl = `${process.env.API_URL}/api/v1`;
console.log('process.env');

const createAuthHeader = (token) => {
  return {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  };
};

const useOrganizationStore = create(immer((set) => ({
  organizations: [],
  uploads: [],

  // Methods to manipulate the state
  setOrganizations: (organizations) => {
    set((state) => {
      state.organizations = organizations;
    });
  },

  setUploads: (uploads) => {
    set((state) => {
      state.uploads = uploads;
    });
  },

  // Asynchronous actions
  getAllOrganizations: async () => {
    try {
      const { data } = await axios.get(`${baseUrl}/organizations`);
      set((state) => {
        state.organizations = data.result;
      });
    } catch (error) {
      console.error("Error in getAllOrganizations:", error);
    }
  },

  createStripeConnectLink: (getToken) => {
    return async (adminId, email, business_type = 'individual', cb, organizationId?, returnUrl?) => {
      try {
        const token = await getToken();
        const { data } = await axios.post(`${baseUrl}/organizations/stripeConnect`, { adminId, email, business_type, organizationId, returnUrl, refreshUrl: returnUrl }, createAuthHeader(token));
        cb && await cb(data.result);
        return data;
      } catch (error) {
        // Surface the server error — a swallowed catch here meant clicking
        // Connect showed NOTHING on any HTTP failure.
        console.error("Error in createStripeConnectLink:", error);
        const message = error?.response?.data?.error || 'Could not start payment setup.';
        showError('Error', message);
        return { error: message };
      }
    }
  },

  // Account Session for EMBEDDED Connect onboarding (ADR 0044) — creates the
  // Express account on first call (email/business_type), then returns
  // { clientSecret, publishableKey, accountId } for @stripe/connect-js.
  createStripeAccountSession: (getToken) => {
    return async (organizationId, payload = {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.post(
          `${baseUrl}/organizations/${organizationId}/stripeAccountSession`,
          payload,
          createAuthHeader(token)
        );
        return data.result; // { clientSecret, publishableKey, accountId }
      } catch (error) {
        console.error("Error in createStripeAccountSession:", error);
        const message = error?.response?.data?.error || 'Could not start payment setup.';
        showError('Error', message);
        return null;
      }
    }
  },

  // Single-use Stripe Express dashboard login link — minted per click.
  getStripeDashboardLink: (getToken) => {
    return async (organizationId) => {
      try {
        const token = await getToken();
        const { data } = await axios.post(`${baseUrl}/organizations/${organizationId}/stripeDashboardLink`, {}, createAuthHeader(token));
        return data.result; // { url }
      } catch (error) {
        console.error("Error in getStripeDashboardLink:", error);
        showError('Error', error?.response?.data?.error || 'Could not open your payout account.');
        return null;
      }
    }
  },

  // Live Connect status (GET /organizations/:id/stripeStatus — ADR 0040).
  // Silent on failure: this runs on background focus refetches.
  getStripeStatus: (getToken) => {
    return async (organizationId) => {
      try {
        const token = await getToken();
        const { data } = await axios.get(`${baseUrl}/organizations/${organizationId}/stripeStatus`, createAuthHeader(token));
        return data.result;
      } catch (error) {
        console.error("Error in getStripeStatus:", error);
        return null;
      }
    }
  },

  // ————— Custom domain lifecycle (INT-0007) —————
  // set/status/remove hit the dedicated endpoints (the generic org update
  // strips customDomain). Errors THROW so the Settings card can render the
  // server's readable message; status is silent-null like getStripeStatus.
  setCustomDomain: (getToken) => {
    return async (organizationId, hostname) => {
      const token = await getToken();
      try {
        const { data } = await axios.post(`${baseUrl}/organizations/${organizationId}/customDomain`, { hostname }, createAuthHeader(token));
        return data.result;
      } catch (error) {
        const message = error?.response?.data?.error || 'Could not set up the domain.';
        throw new Error(message);
      }
    }
  },
  getCustomDomainStatus: (getToken) => {
    return async (organizationId) => {
      try {
        const token = await getToken();
        const { data } = await axios.get(`${baseUrl}/organizations/${organizationId}/customDomain/status`, createAuthHeader(token));
        return data.result;
      } catch (error) {
        console.error("Error in getCustomDomainStatus:", error);
        return null;
      }
    }
  },
  removeCustomDomain: (getToken) => {
    return async (organizationId) => {
      const token = await getToken();
      try {
        const { data } = await axios.delete(`${baseUrl}/organizations/${organizationId}/customDomain`, createAuthHeader(token));
        return data.result;
      } catch (error) {
        const message = error?.response?.data?.error || 'Could not remove the domain.';
        throw new Error(message);
      }
    }
  },

  getOrganizationById: async (id, cb) => {
    try {
      const { data } = await axios.get(`${baseUrl}/organizations/${id}`);
      cb && await cb(data.result);
      return data;
    } catch (error) {
      console.error("Error in getOrganizationById:", error);
      cb && cb(null);
    }
  },
  getOrganizationByName: async (name, cb) => {
    try {
      const { data } = await axios.get(`${baseUrl}/organizations/search/?name=${name}`);
      set((state) => {
        state.organizations.push(data.result);
      });
      cb && await cb(data.result);
      return data;
    } catch (error) {
      console.error("Error in getOrganizationByName:", error);
    }
  },
  getOrganizationByDisplayName: async (name, cb) => {
    try {
      const { data } = await axios.get(`${baseUrl}/organizations/displayName/?name=${name}`);
      set((state) => {
        state.organizations.push(data.result);
      });
      cb && await cb(data.result);
      return data;
    } catch (error) {
      console.error("Error in getOrganizationByDisplayName:", error);
    }
  },

  setOrganizationBanner: async (hasBanner, organizationName) => {
    try {
      const { data } = await axios.post(`${baseUrl}/organizations/d/${organizationName}/banner`, { hasBanner });
    } catch (error) {
      console.error("Error in setOrganizationBanner:", error);
    }
  },

  createOrganization: async (organizationName, slug, userId, token, parentOrganizationId, cb, sports = ['basketball'], isTournament = 0, location = '', skipSampleCompetition = 0) => {
    try {
      const { data } = await axios.post(`${baseUrl}/organizations`, {
        slug,
        leagueName: organizationName,
        userId,
        bootstrap: true,
        parentOrganizationId,
        sports,
        isTournament,
        location,
        // INT-0011 tier 2: the wizard's confirm step will build the CARRIED
        // structure — skip the bootstrap sample competition + its ADR 0038
        // registration so the new org doesn't start with a confusing
        // duplicate. Home content/starter posts still bootstrap.
        skipSampleCompetition,
        // Stamped onto the bootstrap-created registration (ADR 0038). Set here
        // in the store so every caller (CreateOrganizationModal, legacy
        // create-league-modal) gets it without signature churn.
        timeZone: browserTimeZone(),
      }, createAuthHeader(token));
      showSuccess("Success", "Successfully Created");
      set((state) => {
        state.organizations.push(data.result);
      });
      cb && await cb(data.result, null);
      return data;
    } catch (error) {
      console.error("Error in createOrganization:", error);
      const message = error?.response?.data?.error || "Failed to create organization";
      showError("Error", message);
      cb && await cb(null, message);
      return { error: message };
    }
  },

  // Get Started "Looks good" (ADR 0038) — allow-listed stepsTaken stamp.
  markOrgStep: (getToken) => async (orgId, step) => {
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/organizations/${orgId}/steps`, { step }, createAuthHeader(token));
      return data;
    } catch (error) {
      console.error("Error in markOrgStep:", error);
      showError("Error", "Could not save your progress");
      return null;
    }
  },

  // INT-0028 finish screen: set-once "link shared" marker. Silent — the
  // marker is analytics, never something the organizer should see fail.
  markLinkShared: (getToken) => async (orgId) => {
    try {
      const token = await getToken();
      const { data } = await axios.post(`${baseUrl}/organizations/${orgId}/funnel-markers/link-shared`, {}, createAuthHeader(token));
      return data?.result || null;
    } catch (error) {
      console.error("Error in markLinkShared:", error);
      return null;
    }
  },

  // INT-0028 finish screen: "Email me this link". Rethrows so the page can
  // map a 429 (throttled) to its own toast.
  sendShareLinkEmail: (getToken) => async (orgId) => {
    const token = await getToken();
    const { data } = await axios.post(`${baseUrl}/organizations/${orgId}/share-link-email`, {}, createAuthHeader(token));
    return data?.result || null;
  },

  // INT-0040: change the tenant subdomain. Rethrows so the field can map the
  // server's 400/409/429 messages to toasts.
  changeSlug: (getToken) => async (orgId, slug) => {
    const token = await getToken();
    const { data } = await axios.put(`${baseUrl}/organizations/${orgId}/slug`, { slug }, createAuthHeader(token));
    if (data?.status === 0) throw new Error(data?.error || 'Could not change your URL.');
    return data?.result || null;
  },

  updateOrganization: (getToken) => {
    return async (organization, cb = () => {}) => {
      try {
        const token = await getToken();
        const { data } = await axios.put(`${baseUrl}/organizations/d/${organization._id}`, organization, createAuthHeader(token));
        cb && await cb(data.result);
        return data;
      } catch (error) {
        console.error("Error in updateOrganization:", error);
      }
    }
  },

  postOrganizationBanner: async (image, organizationName) => {
    try {
      let formData = new FormData();
      if (image && image.length) {
        formData.append("upload", image[0]);
        await axios.post(`${baseUrl}/organizations/d/${organizationName}/bannerupload`, formData, {
          headers: { "Content-Type": `multipart/form-data; boundary=${formData._boundary}` },
        });
      }
    } catch (error) {
      console.error("Error in postOrganizationBanner:", error);
    }
  },

  postOrganizationImage: async (images, organizationName) => {
    try {
      if (images && images.length) {
        for (let image of images) {
          let formData = new FormData();
          formData.append("upload", image);
          await axios.post(`${baseUrl}/organizations/d/${organizationName}/imageupload`, formData, {
            headers: { "Content-Type": `multipart/form-data; boundary=${formData._boundary}` },
          });
        }
        showSuccess("Success", "Successfully Uploaded");
      }

      const { data } = await axios.get(`${baseUrl}/organizations/${organizationName}/alluploads`);
      set((state) => {
        state.uploads = data.result.uploads;
      });
    } catch (error) {
      console.error("Error in postOrganizationImage:", error);
    }
  },

  deleteOrganization: async (organization) => {
    try {
      await axios.delete(`${baseUrl}/organizations/${organization._id}`);
      showSuccess("Success", "Successfully Removed");
      set((state) => {
        state.organizations = state.organizations.filter((l) => l._id !== organization._id);
      });
    } catch (error) {
      console.error("Error in deleteOrganization:", error);
    }
  },

})));

export default useOrganizationStore;
